import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Visibility,
  Edit,
  // PersonAdd,
  AttachFile,
  FileDownload,
} from '@mui/icons-material';
import ModalUserEdit from '../../components/ModalUserEdit';
import ModalConfirm from '../../components/ModalConfirm';
import ModalResult from '../../components/ModalResult';
import ModalFileSelect from '../../components/ModalFileSelect';
import { useAuth } from '../../contexts/AuthContext';
import { useAdmin } from '../../contexts/AdminContext';
import LoginIcon from '@mui/icons-material/Login';
import getConfig from '../../config/app-config';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import { exportUsersToCSV } from '../../utils/exportCSV/exportUsers';

dayjs.locale('it');

const UserManagement = () => {
  const navigate = useNavigate();
  const { users, updateUser, deleteUser } = useAdmin();
  const config = getConfig();
  const [editModal, setEditModal] = React.useState({ open: false, user: null });
  const [viewModal, setViewModal] = React.useState({ open: false, user: null });
  const [confirmModal, setConfirmModal] = React.useState({ open: false, user: null });
  const [resultModal, setResultModal] = React.useState({ open: false, message: '', severity: 'info' });
  const [form, setForm] = React.useState({ name: '', surname: '', roles: [], level: '' });
  const [fileModal, setFileModal] = React.useState({ open: false, user: null });
  const { login, isDev } = useAuth();
  const [loginLoadingId, setLoginLoadingId] = React.useState(null);
  const [loginError, setLoginError] = React.useState('');
  const [loginSuccess, setLoginSuccess] = React.useState('');

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin':
        return 'error';
      case 'teacher':
        return 'warning';
      case 'member':
        return 'primary';
      default:
        return 'default';
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'admin':
        return 'Amministratore';
      case 'teacher':
        return 'Docente';
      case 'member':
        return 'Membro';
      default:
        return role;
    }
  };

  const handleOpenEdit = (user) => {
    setEditModal({ open: true, user });
    setForm({ name: user.name, surname: user.surname, roles: user.roles, level: user.level || '' });
  };
  const handleCloseEdit = () => {
    setEditModal({ open: false, user: null });
    setForm({ name: '', surname: '', roles: [], level: '' });
  };
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      // console.log('form', form);
      // console.log('editModal.user', editModal.user);
      const result = await updateUser({
        _id: editModal.user._id,
        name: form.name,
        surname: form.surname,
        roles: form.roles,
        level: form.level,
      });

      if (result.success) {
        setResultModal({ open: true, message: 'Utente aggiornato con successo!', severity: 'success' });
        handleCloseEdit();
      } else {
        setResultModal({ open: true, message: 'Errore durante la modifica utente', severity: 'error' });
      }
    } catch (e) {
      setResultModal({ open: true, message: 'Errore durante la modifica utente', severity: 'error' });
    }
  };

  const handleOpenDelete = (user) => setConfirmModal({ open: true, user });
  const handleCloseDelete = () => setConfirmModal({ open: false, user: null });
  const handleDelete = async () => {
    try {
      const result = await deleteUser(confirmModal.user._id);
      if (result.success) {
        setResultModal({ open: true, message: 'Utente eliminato con successo!', severity: 'success' });
      } else {
        setResultModal({ open: true, message: "Errore durante l'eliminazione utente", severity: 'error' });
      }
    } catch (e) {
      setResultModal({ open: true, message: "Errore durante l'eliminazione utente", severity: 'error' });
    } finally {
      handleCloseDelete();
    }
  };
  const handleCloseResult = () => setResultModal({ open: false, message: '', severity: 'info' });
  const handleOpenView = (user) => {
    // console.log('config.WITH_USER_AND_TEACHER_DETAILS', config.WITH_USER_AND_TEACHER_DETAILS);
    if (config.WITH_USER_AND_TEACHER_DETAILS) {
      // Se l'utente è un teacher, vai alla pagina teacher-details, altrimenti user-details
      if (user.roles && user.roles.includes('teacher')) {
        navigate(`/teacher-details/${user._id}`);
      } else {
        navigate(`/user-details/${user._id}`);
      }
    } else {
      setViewModal({ open: true, user });
    }
  };
  const handleCloseView = () => setViewModal({ open: false, user: null });

  const handleOpenFileModal = (user) => {
    setFileModal({ open: true, user });
  };

  const handleCloseFileModal = () => {
    setFileModal({ open: false, user: null });
  };

  const handleFilesUploaded = (uploadedFiles) => {
    setResultModal({
      open: true,
      message: `${uploadedFiles.length} file caricati con successo per ${fileModal.user?.name} ${fileModal.user?.surname}!`,
      severity: 'success',
    });
    handleCloseFileModal();
  };

  const handleLoginAsUser = async (user) => {
    setLoginLoadingId(user._id);
    setLoginError('');
    setLoginSuccess('');
    try {
      const result = await login(user.email, 'xxxxxx');
      if (result.success) {
        setLoginSuccess(`Login effettuato come ${user.email}`);
      } else {
        setLoginError(result.error || 'Errore durante il login');
      }
    } catch (err) {
      setLoginError(err?.response?.data?.message || 'Errore durante il login');
    } finally {
      setLoginLoadingId(null);
      setTimeout(() => {
        setLoginError('');
        setLoginSuccess('');
      }, 2500);
    }
  };

  const handleExportCSV = () => {
    exportUsersToCSV(users);
  };

  const userColumns = [
    {
      field: 'fullName',
      headerName: 'Nome',
      flex: 1.5,
      minWidth: 180,
      resizable: true,
      renderCell: (params) => {
        const name = params.row.name || '';
        const surname = params.row.surname || '';
        const fullName = `${name} ${surname}`.trim() || 'N/A';
        const email = params.row.email || '';
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant='body2'>{fullName}</Typography>
            <Typography variant='caption' color='text.secondary' sx={{ fontSize: '0.75rem' }}>
              {email}
            </Typography>
          </Box>
        );
      },
      valueGetter: (_, user) => {
        const name = user.name || '';
        const surname = user.surname || '';
        const fullName = `${name} ${surname}`.trim() || 'N/A';
        const email = user.email || '';
        return `${fullName} ${email}`.toLowerCase();
      },
    },
    {
      field: 'roles',
      headerName: 'Ruolo',
      flex: 1,
      minWidth: 120,
      resizable: true,
      renderCell: (params) => {
        const roles = params.row.roles || [];
        return (
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
            {roles.map((role) => (
              <Chip sx={{ mt:  1.5 }} key={role} label={getRoleLabel(role)} color={getRoleColor(role)} size='small' />
            ))}

          </Box>
        );
      },
      valueGetter: (_, user) => {
        // Sistema di priorità per ordinamento: admin (1) > teacher (2) > member (3)
        const rolePriority = { admin: 1, teacher: 2, member: 3 };
        const role = user.roles?.[0] || 'member';
        return rolePriority[role] || 3;
      },
    },
    {
      field: 'level',
      headerName: 'Anno di Corso',
      flex: 0.8,
      minWidth: 120,
      resizable: true,
      renderCell: (params) => {
        // Mostra solo per membri
        const isMember = params.row.roles?.includes('member');
        if (!isMember) {
          return <Chip label='N/A' color='default' size='small' />;
        }
        if (params.value === 0) {
          return <Chip label='Da assegnare' color='info' size='small' />;
        }
        if (params.value >= 6) {
          return 'Oltre il 5° anno';
        }
        return `${params.value}° anno`;
      },
      valueGetter: (_, user) => {
        // Se non è un membro o non ha un level, restituisce un valore alto per metterlo alla fine
        const isMember = user.roles?.includes('member');
        if (!isMember) {
          return 999;
        }
        // Per i valori numerici, usa il valore stesso per l'ordinamento
        return parseInt(user.level);
      },
    },
    {
      field: 'flagPicture',
      headerName: 'Foto',
      flex: 0.6,
      minWidth: 80,
      resizable: true,
      renderCell: (params) => {
        const hasPicture = params.row.flagPicture;
        return <Chip label={hasPicture ? 'Si' : 'No'} color={hasPicture ? 'success' : 'default'} size='small' />;
      },
      valueGetter: (_, user) => {
        return user.flagPicture ? 1 : 0;
      },
    },
    // {
    //   field: 'membershipStatus',
    //   headerName: 'Stato Membri',
    //   width: 150,
    //   renderCell: (params) => (
    //     <Chip
    //       label={getMembershipStatusLabel(params.value)}
    //       color={getMembershipStatusColor(params.value)}
    //       size="small"
    //     />
    //   )
    // },
    {
      field: 'actions',
      headerName: 'Azioni',
      flex: 1,
      minWidth: 120,
      resizable: true,
      renderCell: (params) => (
        <Box>
          <IconButton size='small' color='primary' onClick={() => handleOpenView(params.row)}>
            <Visibility />
          </IconButton>
          <IconButton size='small' color='primary' onClick={() => handleOpenEdit(params.row)}>
            <Edit />
          </IconButton>
          <Tooltip title='Allega file'>
            <IconButton size='small' color='info' onClick={() => handleOpenFileModal(params.row)}>
              <AttachFile />
            </IconButton>
          </Tooltip>
          {isDev && (
            <Tooltip title='Login come questo utente'>
              <span>
                <IconButton size='small' color='secondary' onClick={() => handleLoginAsUser(params.row)} disabled={loginLoadingId === params.row._id} sx={{ ml: 0.5 }}>
                  {loginLoadingId === params.row._id ? <CircularProgress size={18} /> : <LoginIcon />}
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant='h6'>Gestione Utenti</Typography>
        <Button variant="outlined" startIcon={<FileDownload />} onClick={handleExportCSV}>
          Esporta CSV
        </Button>
      </Box>
      <DataGrid
        rows={users}
        columns={userColumns}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 25, page: 0 },
          },
          sorting: {
            sortModel: [{ field: 'roles', sort: 'asc' }],
          },
        }}
        rowsPerPageOptions={[10, 25, 50]}
        disableSelectionOnClick
        // NON mettere disableColumnMenu
        hideFooterPagination={users.length <= 10}
      />
      <ModalUserEdit open={editModal.open} onClose={handleCloseEdit} onSubmit={handleEditSubmit} onDeleteUser={handleOpenDelete} mode='edit' form={form} setForm={setForm} user={editModal.user} />
      <ModalUserEdit open={viewModal.open} onClose={handleCloseView} mode='view' form={{}} setForm={() => {}} user={viewModal.user} />
      <ModalConfirm open={confirmModal.open} title='Conferma eliminazione' message={<Typography variant='body1'>Sei sicuro di voler eliminare l'utente "{confirmModal.user?.name} {confirmModal.user?.surname}"? <br /> <span style={{ color: 'red' }}>Questa azione è irreversibile</span></Typography>} onConfirm={handleDelete} onCancel={handleCloseDelete} />
      <ModalFileSelect open={fileModal.open} onClose={handleCloseFileModal} userId={fileModal.user?._id} onFilesUploaded={handleFilesUploaded} />
      <ModalResult open={resultModal.open} onClose={handleCloseResult} message={resultModal.message} severity={resultModal.severity} />
      {loginError && <ModalResult open={!!loginError} onClose={() => setLoginError('')} message={loginError} severity='error' />}
      {loginSuccess && <ModalResult open={!!loginSuccess} onClose={() => setLoginSuccess('')} message={loginSuccess} severity='success' />}
    </Box>
  );
};

export default UserManagement;
